import isWindow from '../isWindow';
import isWindowProp from '../isWindowProp';
import { Bling as GPT } from 'react-gpt';
import * as R from 'ramda';
import slugify from 'slugify';
import { getPagePathname } from 'utils/getPagePathname';

// Allo us to slugify the same way we do on Search API.
const slugifyRemoveSpecialChars = _ =>
  slugify(_, { remove: /[*%+~,#./''"~=\]!:@?;|[()]/g });
const notNil = R.compose(R.not, R.isNil);
const toLowerCase = R.when(notNil, R.toLower);
const toSlugify = R.when(notNil, slugifyRemoveSpecialChars);
const toSlug = R.compose(toSlugify, toLowerCase);

const isMobile = () => isWindow() && isWindowProp('innerWidth') <= 991;
const pagePath = getPagePathname();
const isNotUsedPage = pagePath !== 'used-cars';

let advertValue = 'used-cars';

if (pagePath === 'new-cars-in-stock') {
  advertValue = 'new-cars-in-stock';
}

if (pagePath === 'car-leasing-deals') {
  advertValue = 'car-leasing-deals';
}

const pType = isNotUsedPage ? 'new' : 'used';

const getMakeModelForAd = (make, model) => {
  if (!make) {
    return '';
  }

  const cleanMake = toSlug(make);
  const cleanModel = model ? toSlug(model) : '';

  return cleanMake && cleanModel
    ? `/${cleanMake}/${cleanModel}`
    : `/${cleanMake}`;
};

const getSiteForAds = () => {
  if (process.env.CLASSIFIEDS_SITE === 'autocar') {
    return isMobile() ? 'autocar.m' : 'autocar';
  }

  return isMobile() ? 'whatcar.m' : 'whatcar';
};

export const adUnitPageNames = {
  home: 'home',
  results: 'results',
  detail: 'vehicle-detail-page'
};

/**
 * Creates the ad unit path.
 * @param {object} param0 An object containing the page, make and model.
 * @returns {string} The ad unit path.
 */
exports.createAdUnitPath = ({ page, make = '', model = '' }: Object) => {
  const site = getSiteForAds();
  const makeModel = getMakeModelForAd(make, model);
  return `8527/${site}/${advertValue}/${page}${makeModel}`;
};

/**
 * The slot definitions, containing ids, targeting and size mapping information per slot.
 */
export const slots = {
  header: {
    id: 'gpt_7294_masthead1',
    targeting: { pos: 'wide1' },
    sizeMapping: [
      {
        viewport: [992, 0],
        slot: [
          [992, 115],
          [970, 250],
          [728, 90],
          [970, 251],
          [728, 91]
        ]
      }
    ]
  },
  sidenav: {
    id: 'gpt_7294_mpu1',
    targeting: { pos: 'mpu1' },
    sizeMapping: [
      {
        viewport: [992, 0],
        slot: [
          [300, 600],
          [300, 601],
          [300, 350],
          [300, 251],
          [1, 1]
        ]
      }
    ]
  },
  gallery: {
    id: 'gpt_7294_mpu_classifieds_gallery',
    targeting: { pos: 'mpu1' },
    sizeMapping: [
      // Purposely disallowing ads on mobile.
      {
        viewport: [991, 0],
        slot: []
      },
      {
        viewport: [992, 0],
        slot: [
          [300, 250],
          // TODO: commenting out double MPU sizes until we re-work the gallery layout.
          // [300, 600],
          [300, 251]
          // TODO: commenting out double MPU sizes until we re-work the gallery layout.
          // [300, 601]
        ]
      }
    ]
  },
  result_first: {
    id: 'gpt_7294_result_first',
    targeting: { pos: 'lmpu1' },
    sizeMapping: [{ viewport: [991, 0], slot: [300, 250] }]
  },
  result_last: {
    id: 'gpt_7294_result_last',
    targeting: { pos: 'lmpu2' },
    sizeMapping: [{ viewport: [991, 0], slot: [300, 251] }]
  },
  overlay: {
    id: 'gpt_7294_overlay',
    targeting: { pos: 'overlay' },
    sizeMapping: [{ viewport: [991, 0], slot: [1, 1] }],
    outOfPage: true
  },
  overlayMobile: {
    id: 'gpt_7294_overlay',
    targeting: { pos: 'overlay-m1' },
    sizeMapping: [{ viewport: [991, 0], slot: [1, 1] }],
    outOfPage: true
  },
  overlay2: {
    id: 'gpt_7294_overlay2',
    targeting: { pos: 'overlay2' },
    sizeMapping: [{ viewport: [991, 0], slot: [1, 3] }]
  },
  overlay2Mobile: {
    id: 'gpt_7294_overlay2',
    targeting: { pos: 'overlay-m2' },
    sizeMapping: [{ viewport: [991, 0], slot: [1, 3] }]
  },
  banner: {
    id: 'gpt_7294_banner2',
    targeting: { pos: 'wide-m2' },
    sizeMapping: [
      {
        viewport: [991, 0],
        slot: [
          [320, 50],
          [320, 52],
          [320, 100]
        ]
      }
    ]
  },
  topMpu: {
    id: 'gpt_7294_mpu1',
    targeting: { pos: 'mpu-m1' },
    sizeMapping: [
      {
        viewport: [991, 0],
        slot: [[300, 250], [300, 600], [300, 251], [300, 603], 'fluid', [1, 1]]
      }
    ]
  },
  flexible: {
    id: 'gpt_7294_flexible1',
    targeting: { pos: 'mpu1' },
    sizeMapping: [
      {
        viewport: [991, 0],
        slot: [
          [300, 250],
          [300, 600],
          [300, 251],
          [300, 601]
        ]
      }
    ]
  }
};

/**
 * Gets the default page-level targets that are common across all pages.
 * @returns {object} The common page-level targets.
 */
export const getCommonTargets = () => ({
  site: process.env.STACK_NAME === 'prod' ? 'newwhatcar' : 'test',
  screensize: window && window.screen && window.screen.width.toString(),
  p_type: pType,
  n_sec2: advertValue,
  p_cat: ''
});

/**
 * Loops around a key-value object and sets DFP targets accordingly.
 * @param {object} keys An object whose key is the target name and whose value is the value to set.
 * @returns {void}
 */
export const setDfpTargeting = keys => {
  R.keys(keys).forEach(key =>
    keys[key] ? GPT.setTargeting(key, toSlug(keys[key])) : ''
  );
};

/**
 * Combines the common page-level targets with any additional ones and sets in DFP.
 * @param {object} additionalTargets Additional key-value targeting.
 * @returns {void}
 */
export const setPageTargeting = (additionalTargets = {}) => {
  const keyValueTargets = {
    ...exports.getCommonTargets(),
    ...additionalTargets
  };
  exports.setDfpTargeting(keyValueTargets);
};

/**
 * Sets the page-level targeting for the Home page.
 * @returns {void}
 */
exports.targetingHome = () => {
  const keys = {
    n_sec3: adUnitPageNames['home']
  };
  exports.setPageTargeting(keys);
};

/**
 * Sets the page-level targeting for the Results page.
 * @param {string} make The make.
 * @param {string} model The model.
 * @returns {void}
 */
exports.targetingResults = (make, model) => {
  make = make || 'home';
  model = model || '';

  const keys = {
    n_sec3: adUnitPageNames['results'],
    p_manu: make,
    p_model: model
  };
  exports.setPageTargeting(keys);
};

/**
 * Sets the page-level targeting for the Detail page.
 * @param {object} derivative A derivative.
 * @returns {void}
 */
exports.targetingDetail = derivative => {
  const keys = {
    n_sec3: adUnitPageNames['detail'],
    p_manu: derivative ? derivative.make : '',
    p_model: derivative ? derivative.range : '',
    p_style: derivative ? derivative.bodyStyle : ''
  };
  exports.setPageTargeting(keys);
};



// WEBPACK FOOTER //
// ./src/Common/ads/index.js